import { publishEventToSDK } from "@repo/sdk/publisher";
import { STATSIG_EVENTS, useStatsigClient } from "@repo/statsig";
import { BRAND } from "@repo/tailwind-config/brand";
import React from "react";
import { BagIcon } from "src/components/icons/bag-icon";
import KinsaSafeAreaBlur from "src/components/KinsaSafeAreaBlur";
import { HeadlineText, SubheadlineText } from "src/components/KinsaTypography";

const isOwlet = BRAND === "owlet";

interface PaymentHeaderProps {
  titleText: string;
  bodyText: string;
  onBack: () => void;
  backLabel?: string;
}

const BackToProductsButton = ({
  onBack,
  backLabel = "BACK TO PRODUCTS",
}: {
  onBack: () => void;
  backLabel?: string;
}) => {
  const { logEvent } = useStatsigClient();

  const onBackToProducts = () => {
    logEvent({
      eventName: STATSIG_EVENTS.BUTTON_PRESS,
      value: "back_to_products",
      metadata: { button_text: backLabel },
    });
    onBack();
  };

  return (
    <button onClick={onBackToProducts}>
      <SubheadlineText className="text-primary">{backLabel}</SubheadlineText>
    </button>
  );
};

const CancelButton = ({ onBack }: { onBack: () => void }) => {
  const { logEvent } = useStatsigClient();

  const onClose = () => {
    logEvent({
      eventName: STATSIG_EVENTS.UI_DISMISSED,
      value: "checkout",
      metadata: { reason: "cancel" },
    });
    publishEventToSDK({ topic: "dismiss", data: "success" });
    onBack();
  };

  return (
    <button onClick={onClose}>
      {isOwlet ? (
        <span className="font-sans font-semibold text-[16px] leading-[24px] text-primary">
          Cancel
        </span>
      ) : (
        <SubheadlineText className="text-primary">CANCEL</SubheadlineText>
      )}
    </button>
  );
};

const HeaderContent = ({
  titleText,
  bodyText,
}: Pick<PaymentHeaderProps, "titleText" | "bodyText">) => {
  if (isOwlet) {
    return (
      <div className="flex flex-col gap-[8px] mt-[12px]">
        <div className="flex items-center gap-[8px]">
          <BagIcon />
          <h1 className="font-heading font-semibold text-[26px] leading-[32px] text-white flex-1">
            {titleText}
          </h1>
        </div>
        <p className="font-sans text-[16px] leading-[24px] text-on-surface">
          {bodyText}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <div className="flex flex-row gap-[8px] items-center">
        <HeadlineText className="leading-loose">{titleText}</HeadlineText>
      </div>
      <SubheadlineText>{bodyText}</SubheadlineText>
    </div>
  );
};

const PaymentHeader: React.FC<PaymentHeaderProps> = ({
  titleText,
  bodyText,
  onBack,
  backLabel,
}) => {
  return (
    <div className="flex flex-col px-[24px]">
      <KinsaSafeAreaBlur />
      <div className={isOwlet ? "flex justify-end" : "flex justify-between"}>
        {!isOwlet && <BackToProductsButton onBack={onBack} backLabel={backLabel} />}
        <CancelButton onBack={onBack} />
      </div>
      <HeaderContent titleText={titleText} bodyText={bodyText} />
    </div>
  );
};

export default PaymentHeader;
